import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { motion } from 'framer-motion';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconFileText,
  IconRefresh,
  IconRobot,
  IconSparkles,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';

const ACCEPT =
  '.pdf,.docx,.doc,.txt,.md,.csv,.xlsx,.xls,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'text/plain,text/markdown,text/csv,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const GEMINI_MODELS = [
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.0-flash',
];
const OPENAI_MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini'];
const NVIDIA_MODELS = [
  'moonshotai/kimi-k3',
  'nvidia/nemotron-3-ultra-550b-a55b',
  'meta/llama-3.3-70b-instruct',
  'deepseek-ai/deepseek-r1',
];

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const comma = r.indexOf(',');
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: 'queued', color: 'gray' },
  processing: { label: 'extracting', color: 'blue' },
  ready: { label: 'ready', color: 'green' },
  failed: { label: 'failed', color: 'red' },
};

function AgentDetail() {
  const { agentId } = Route.useParams();
  const id = Number(agentId);
  const utils = trpc.useUtils();

  const detail = trpc.aiAgents.get.useQuery(
    { id },
    {
      // Poll while any file is still being extracted so the badges flip live.
      refetchInterval: (query) => {
        const files = query.state.data?.files;
        const busy = files?.some(
          (f) => f.extractStatus === 'pending' || f.extractStatus === 'processing',
        );
        return busy ? 4000 : false;
      },
    },
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [persona, setPersona] = useState('');
  const [useBusiness, setUseBusiness] = useState(true);
  const [statusActive, setStatusActive] = useState(true);
  const [provider, setProvider] = useState<'' | 'gemini' | 'openai' | 'nvidia'>('');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState<number | ''>('');
  const [memoryWindow, setMemoryWindow] = useState<number | ''>('');
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setName(d.name);
    setDescription(d.description ?? '');
    setPersona(d.persona ?? '');
    setUseBusiness(d.useBusinessDetails);
    setStatusActive(d.status === 1);
    setProvider((d.provider as 'gemini' | 'openai' | 'nvidia' | null) ?? '');
    setModel(d.model ?? '');
    setTemperature(d.temperature ?? '');
    setMemoryWindow(d.memoryWindow ?? '');
  }, [detail.data]);

  const save = trpc.aiAgents.update.useMutation({
    onSuccess: () => {
      void utils.aiAgents.get.invalidate({ id });
      void utils.aiAgents.list.invalidate();
      notifications.show({ message: 'Agent saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const onSave = () =>
    save.mutate({
      id,
      name: name.trim(),
      description: description.trim() || null,
      persona,
      useBusinessDetails: useBusiness,
      status: statusActive ? 1 : 2,
      provider: provider === '' ? null : provider,
      model: model.trim() || null,
      temperature: temperature === '' ? null : Number(temperature),
      memoryWindow: memoryWindow === '' ? null : Number(memoryWindow),
    });

  if (detail.isPending) return <Skeleton height={500} />;
  if (detail.error || !detail.data) {
    return (
      <Stack gap="md">
        <BackLink />
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {detail.error?.message ?? 'Agent not found.'}
        </Alert>
      </Stack>
    );
  }

  const modelOptions =
    provider === 'openai' ? OPENAI_MODELS : provider === 'nvidia' ? NVIDIA_MODELS : GEMINI_MODELS;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <BackLink />
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconRobot size={24} color="var(--mantine-color-blue-6)" />
            <Title order={2}>{detail.data.name}</Title>
            <Badge variant="light" color={statusActive ? 'green' : 'gray'}>
              {statusActive ? 'active' : 'inactive'}
            </Badge>
          </Group>
          <Button leftSection={<IconCheck size={16} />} loading={save.isPending} onClick={onSave}>
            Save
          </Button>
        </Group>

        <Card withBorder padding="lg" radius="md">
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Name"
                required
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <TextInput
                label="Description"
                description="Internal note"
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </Group>

            <Textarea
              label="Business context / persona"
              description="The agent's instructions and business knowledge. This becomes the system prompt."
              placeholder="You are the support assistant for Acme Co. We sell handmade candles. Be warm and concise. Shipping takes 3–5 days. Never promise refunds beyond 30 days."
              minRows={6}
              autosize
              maxRows={16}
              value={persona}
              onChange={(e) => setPersona(e.currentTarget.value)}
            />

            <Switch
              label="Include business details"
              description="Inject your saved business profile (name, address, hours, website) into the agent's context."
              checked={useBusiness}
              onChange={(e) => setUseBusiness(e.currentTarget.checked)}
            />
            <Switch
              label="Active"
              description="Inactive agents are never auto-selected to reply."
              checked={statusActive}
              onChange={(e) => setStatusActive(e.currentTarget.checked)}
            />

            <Button
              variant="subtle"
              size="xs"
              w="fit-content"
              onClick={() => setAdvanced((a) => !a)}
            >
              {advanced ? 'Hide advanced' : 'Advanced (provider, model, temperature)'}
            </Button>
            {advanced && (
              <Stack gap="sm">
                <Divider
                  label="Overrides (optional — else uses your AI & timing defaults)"
                  labelPosition="center"
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Select
                    label="Provider"
                    placeholder="Vendor default"
                    clearable
                    data={[
                      { value: 'gemini', label: 'Google Gemini' },
                      { value: 'openai', label: 'OpenAI' },
                      { value: 'nvidia', label: 'NVIDIA NIM' },
                    ]}
                    value={provider || null}
                    onChange={(v) =>
                      setProvider((v as 'gemini' | 'openai' | 'nvidia' | null) ?? '')
                    }
                  />
                  <Select
                    label="Model"
                    placeholder="Provider default"
                    clearable
                    searchable
                    data={
                      model && !modelOptions.includes(model)
                        ? [model, ...modelOptions]
                        : modelOptions
                    }
                    value={model || null}
                    onChange={(v) => setModel(v ?? '')}
                  />
                  <NumberInput
                    label="Temperature"
                    placeholder="0.7"
                    min={0}
                    max={2}
                    step={0.1}
                    decimalScale={1}
                    value={temperature}
                    onChange={(v) => setTemperature(v === '' ? '' : Number(v))}
                  />
                  <NumberInput
                    label="Memory window"
                    description="Recent messages (2–20)"
                    min={2}
                    max={20}
                    value={memoryWindow}
                    onChange={(v) => setMemoryWindow(v === '' ? '' : Number(v))}
                  />
                </SimpleGrid>
              </Stack>
            )}
          </Stack>
        </Card>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <KnowledgeFiles agentId={id} files={detail.data.files} />
          <TestPanel agentId={id} />
        </SimpleGrid>
      </Stack>
    </motion.div>
  );
}

function BackLink() {
  return (
    <Anchor component={Link} to="/vendor/settings/ai-agents" size="sm">
      <Group gap={4}>
        <IconArrowLeft size={14} />
        Back to agents
      </Group>
    </Anchor>
  );
}

type FileRow = {
  id: number;
  fileName: string;
  fileSize: number;
  extractStatus: string;
  extractError: string | null;
  charCount: number | null;
};

function KnowledgeFiles({ agentId, files }: { agentId: number; files: FileRow[] }) {
  const utils = trpc.useUtils();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = trpc.aiAgents.files.upload.useMutation();
  const remove = trpc.aiAgents.files.delete.useMutation({
    onSuccess: () => {
      void utils.aiAgents.get.invalidate({ id: agentId });
      notifications.show({ message: 'File removed', color: 'blue' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const reextract = trpc.aiAgents.files.reextract.useMutation({
    onSuccess: () => {
      void utils.aiAgents.get.invalidate({ id: agentId });
      notifications.show({ message: 'Re-extracting…', color: 'blue' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  async function handleFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(list)) {
        const base64 = await fileToBase64(file);
        await upload.mutateAsync({
          agentId,
          base64,
          mimeType: file.type || 'application/octet-stream',
          filename: file.name,
        });
      }
      notifications.show({ message: 'Uploaded — extracting text…', color: 'green' });
    } catch (e) {
      notifications.show({ message: (e as Error).message, color: 'red' });
    } finally {
      setUploading(false);
      void utils.aiAgents.get.invalidate({ id: agentId });
    }
  }

  return (
    <Card withBorder padding="lg" radius="md" style={{ height: '100%' }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <IconFileText size={20} color="var(--mantine-color-blue-6)" />
            <Title order={4}>Knowledge files</Title>
          </Group>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            size="xs"
            variant="light"
            leftSection={<IconUpload size={14} />}
            loading={uploading}
            onClick={() => fileRef.current?.click()}
          >
            Add files
          </Button>
        </Group>
        <Text c="dimmed" size="xs">
          PDF, Word, text, CSV, or Excel. We extract the text and feed it to the agent (max 20 MB
          each).
        </Text>

        {files.length === 0 ? (
          <Alert color="gray" variant="light">
            No files yet. Upload product sheets, FAQs, or price lists to ground the agent&apos;s
            answers.
          </Alert>
        ) : (
          <Stack gap="xs">
            {files.map((f) => {
              const st = FILE_STATUS[f.extractStatus] ?? { label: f.extractStatus, color: 'gray' };
              return (
                <Group key={f.id} justify="space-between" wrap="nowrap" gap="xs">
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    <Text size="sm" truncate>
                      {f.fileName}
                    </Text>
                    <Text c="dimmed" size="xs">
                      {formatBytes(f.fileSize)}
                      {f.charCount ? ` · ${f.charCount.toLocaleString()} chars` : ''}
                      {f.extractStatus === 'failed' && f.extractError ? ` · ${f.extractError}` : ''}
                    </Text>
                  </Stack>
                  <Group gap={4} wrap="nowrap">
                    <Badge size="sm" variant="light" color={st.color}>
                      {st.label}
                    </Badge>
                    {f.extractStatus === 'failed' && (
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => reextract.mutate({ fileId: f.id })}
                        title="Retry extraction"
                      >
                        <IconRefresh size={16} />
                      </ActionIcon>
                    )}
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() =>
                        modals.openConfirmModal({
                          title: 'Remove file?',
                          centered: true,
                          children: <Text size="sm">Remove “{f.fileName}” from this agent?</Text>,
                          labels: { confirm: 'Remove', cancel: 'Cancel' },
                          confirmProps: { color: 'red' },
                          onConfirm: () => remove.mutate({ fileId: f.id }),
                        })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function TestPanel({ agentId }: { agentId: number }) {
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState<string | null>(null);

  const test = trpc.aiAgents.test.useMutation({
    onSuccess: (r) => setReply(r.reply),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <Card withBorder padding="lg" radius="md" style={{ height: '100%' }}>
      <Stack gap="sm">
        <Group gap="xs">
          <IconSparkles size={20} color="var(--mantine-color-blue-6)" />
          <Title order={4}>Test the agent</Title>
        </Group>
        <Text c="dimmed" size="xs">
          Send a sample customer message and see how the agent replies using its persona, business
          details, and ready knowledge files. Save your changes first.
        </Text>
        <Textarea
          placeholder="e.g. Do you ship to Canada and how long does it take?"
          minRows={2}
          autosize
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
        />
        <Button
          variant="light"
          loading={test.isPending}
          disabled={!message.trim()}
          onClick={() => test.mutate({ id: agentId, message: message.trim() })}
        >
          Test
        </Button>
        {reply !== null && (
          <Alert color="blue" variant="light" title="Agent reply">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {reply}
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/settings/ai-agents/$agentId')({
  component: AgentDetail,
});
